/**
 * SmartDefaults - Remember User Preferences
 * Saves and retrieves user preferences for a personalized experience
 * Part of the User Love Layer
 */
import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'keikka_smart_defaults';
const LAST_BOOKING_KEY = 'keikka_last_booking';
const SEARCH_HISTORY_KEY = 'keikka_search_history';

/**
 * Default structure for user preferences
 */
const DEFAULT_PREFERENCES = {
  country: '',
  city: '',
  preferred_categories: [],
  notification_email: true,
  notification_sms: false,
  language: 'en',
  last_used_at: null
};

/**
 * Load preferences from localStorage
 */
export const loadSmartDefaults = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_PREFERENCES, ...parsed };
    }
  } catch (error) {
    console.warn('Failed to load smart defaults:', error);
  }
  return DEFAULT_PREFERENCES;
};

/**
 * Save preferences to localStorage
 */
export const saveSmartDefaults = (preferences) => {
  try {
    const updated = {
      ...loadSmartDefaults(),
      ...preferences,
      last_used_at: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return updated;
  } catch (error) {
    console.warn('Failed to save smart defaults:', error);
    return preferences;
  }
};

/**
 * Save last booking for quick rebooking
 */
export const saveLastBooking = (booking) => {
  try {
    localStorage.setItem(LAST_BOOKING_KEY, JSON.stringify({
      ...booking,
      saved_at: new Date().toISOString()
    }));
  } catch (error) {
    console.warn('Failed to save last booking:', error);
  }
};

/**
 * Load last booking
 */
export const loadLastBooking = () => {
  try {
    const stored = localStorage.getItem(LAST_BOOKING_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to load last booking:', error);
  }
  return null;
};

/**
 * Add to search history
 */
export const addToSearchHistory = (search) => {
  try {
    const history = loadSearchHistory();
    const newHistory = [
      { query: search, timestamp: new Date().toISOString() },
      ...history.filter(h => h.query !== search).slice(0, 9)
    ];
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
    return newHistory;
  } catch (error) {
    console.warn('Failed to add to search history:', error);
    return [];
  }
};

/**
 * Load search history
 */
export const loadSearchHistory = () => {
  try {
    const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to load search history:', error);
  }
  return [];
};

/**
 * Clear all smart defaults
 */
export const clearSmartDefaults = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LAST_BOOKING_KEY);
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  } catch (error) {
    console.warn('Failed to clear smart defaults:', error);
  }
};

/**
 * Hook: useSmartDefaults
 * React hook for managing smart defaults in components
 */
export const useSmartDefaults = () => {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [lastBooking, setLastBooking] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load on mount
  useEffect(() => {
    const loaded = loadSmartDefaults();
    setPreferences(loaded);
    setLastBooking(loadLastBooking());
    setIsLoaded(true);
  }, []);

  // Update preferences
  const updatePreferences = useCallback((updates) => {
    const updated = saveSmartDefaults(updates);
    setPreferences(updated);
    return updated;
  }, []);

  // Remember booking
  const rememberBooking = useCallback((booking) => {
    saveLastBooking(booking);
    setLastBooking(booking);
    
    // Also update general preferences from booking
    if (booking.country || booking.city || booking.category) {
      updatePreferences({
        country: booking.country || preferences.country,
        city: booking.city || preferences.city,
        preferred_categories: booking.category 
          ? [booking.category, ...preferences.preferred_categories.filter(c => c !== booking.category)].slice(0, 5)
          : preferences.preferred_categories
      });
    }
  }, [preferences, updatePreferences]);

  // Get suggested category based on history
  const getSuggestedCategory = useCallback(() => {
    if (preferences.preferred_categories.length > 0) {
      return preferences.preferred_categories[0];
    }
    return null;
  }, [preferences.preferred_categories]);

  // Check if user is returning
  const isReturningUser = Boolean(preferences.last_used_at);

  // Get personalized greeting
  const getGreeting = useCallback((language = 'en') => {
    if (!isReturningUser) {
      return {
        en: "Find help fast. No hassle.",
        et: "Leia abi kiirelt. Vaevata.",
        fi: "Löydä apua nopeasti. Vaivatta."
      }[language] || "Find help fast. No hassle.";
    }
    
    return {
      en: "Welcome back! Ready to book?",
      et: "Tere tulemast tagasi! Valmis broneerima?",
      fi: "Tervetuloa takaisin! Valmis varaamaan?"
    }[language] || "Welcome back! Ready to book?";
  }, [isReturningUser]);

  return {
    preferences,
    lastBooking,
    isLoaded,
    isReturningUser,
    updatePreferences,
    rememberBooking,
    getSuggestedCategory,
    getGreeting,
    clearAll: clearSmartDefaults
  };
};

/**
 * Hook: useSearchSuggestions
 * For autocomplete and search history
 */
export const useSearchSuggestions = () => {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    setHistory(loadSearchHistory());
  }, []);

  const addSearch = useCallback((query) => {
    if (query && query.trim()) {
      const updated = addToSearchHistory(query.trim());
      setHistory(updated);
    }
  }, []);

  const getRecentSearches = useCallback((limit = 5) => {
    return history.slice(0, limit).map(h => h.query);
  }, [history]);

  return {
    history,
    addSearch,
    getRecentSearches
  };
};

export default {
  useSmartDefaults,
  useSearchSuggestions,
  loadSmartDefaults,
  saveSmartDefaults,
  saveLastBooking,
  loadLastBooking,
  clearSmartDefaults
};
